/*
 * ADVENTSKALENDER EXCLUSIVE CART — the client-side net.
 * =============================================================================
 *
 * The CH 3PL cannot pick a mixed parcel, so the Adventskalender must be ordered
 * alone. This file enforces that in the storefront. It is the UX layer only: the
 * authoritative block lives in the checkout Validation app (see
 * docs/adventskalender-lock.md §6), which also covers express checkout and the
 * Shop app — paths no theme code can reach.
 *
 * TWO LAYERS, IN THIS ORDER
 *
 *  1. THE DOM LAYER is the one shoppers meet. A capture-phase `submit`/`click`
 *     listener on `document` stops the theme's own handler from ever starting.
 *     Nothing spins, nothing errors, no button flips to "Ausverkauft" — the
 *     shopper just gets the modal. It covers every surface this theme renders,
 *     because all of them submit a real `form[action$="/cart/add"]`
 *     (snippets/buy-buttons.liquid, snippets/quick-add-button.liquid).
 *
 *  2. THE NETWORK LAYER is the backstop for everything else: upsell, bundle and
 *     gift apps that call `/cart/add.js` directly, and the theme's own
 *     script-driven adds (assets/cart-upsells.js, assets/cart-progress.js,
 *     assets/product-bundle.js, assets/promo-products.js). It wraps `fetch` and
 *     `XMLHttpRequest`, matches on the path SUFFIX so language prefixes (/fr,
 *     /it — sheko.ch publishes de, fr and it) cannot slip past, and reads the
 *     cart fresh before it decides.
 *
 * WHY BOTH. Layer 2 alone would have to answer a request it cancelled with a
 * synthesised response, and every consumer parses that differently. Layer 1
 * never fabricates anything: it DEFERS the shopper's own action and, once the
 * conflict is resolved, REPLAYS it — the theme then runs its normal code path
 * with a real server response. Layer 2 only fabricates on the cancel path, and
 * only for callers this theme does not own.
 *
 * WHAT IT NEVER DOES
 *  - It never touches `/cart/update` writes that are not quantity writes. The
 *    attribution writer (assets/cart-attribution.js) and the gift engine
 *    (assets/cart-progress.js) both post to that endpoint; only a payload that
 *    actually moves the calendar's quantity past the limit is considered.
 *  - It never retries a cart write. R-01 stands.
 *  - It never runs at all when the feature is off — the bootstrap snippet does
 *    not even render this file's <script> tag.
 */
(function () {
  'use strict';

  var CONFIG = window.SHEKO_AK_LOCK;
  if (!CONFIG || CONFIG.active !== true) return;

  var VARIANT_ID = Number(CONFIG.variantId) || 0;
  if (!VARIANT_ID) return;

  var MAX_QTY = Number(CONFIG.maxQuantity) || 1;
  var ROUTES = CONFIG.routes || {};
  var STRINGS = CONFIG.strings || {};
  var REMOVED_KEY = 'sheko:adventskalender-removed';

  var nativeFetch = typeof window.fetch === 'function' ? window.fetch.bind(window) : null;

  /* Raised while WE re-issue a shopper's original request. Both layers honour
     it, so a replay can never be intercepted by the code that deferred it. */
  var bypass = 0;

  /* ---------------------------------------------------------------------------
   * 1. CART STATE
   *
   * Seeded from the server render, then kept current from every cart response
   * the wrapper observes — which is all of them, since the wrapper is installed
   * before any caller can capture a native reference. The cache decides only
   * whether a request is WORTH inspecting; a request that is actually blocked is
   * always judged against a fresh `GET /cart.js`.
   * ------------------------------------------------------------------------ */
  var cartState = {
    calendarQuantity: Number(CONFIG.cart && CONFIG.cart.calendarQuantity) || 0,
    otherCount: Number(CONFIG.cart && CONFIG.cart.otherCount) || 0,
    lineKey: (CONFIG.cart && CONFIG.cart.lineKey) || ''
  };

  function adoptCart(cart) {
    if (!cart || !isArray(cart.items)) return null;
    var next = { calendarQuantity: 0, otherCount: 0, lineKey: '' };
    for (var i = 0; i < cart.items.length; i++) {
      var item = cart.items[i];
      if (Number(item.variant_id) === VARIANT_ID) {
        next.calendarQuantity += Number(item.quantity) || 0;
        next.lineKey = item.key || '';
      } else {
        next.otherCount += 1;
      }
    }
    cartState = next;
    return next;
  }

  var pendingCartRead = null;

  function readCart() {
    if (pendingCartRead) return pendingCartRead;
    if (!nativeFetch) return Promise.resolve(null);
    pendingCartRead = nativeFetch(ROUTES.cart + '.js', {
      method: 'GET',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    })
      .then(function (response) {
        return response.ok ? response.json() : null;
      })
      .catch(function () {
        return null;
      })
      .then(function (cart) {
        pendingCartRead = null;
        if (cart) adoptCart(cart);
        /* A failed read falls back to the cache rather than to nothing: the
           cache is the server snapshot plus every mutation observed since. */
        return cart;
      });
    return pendingCartRead;
  }

  /* ---------------------------------------------------------------------------
   * 2. ENDPOINT MATCHING — suffix, never equality.
   * ------------------------------------------------------------------------ */
  function endpointOf(url) {
    var path;
    try {
      path = new URL(String(url), window.location.origin).pathname;
    } catch (error) {
      return null;
    }
    path = path.replace(/\.js$/, '').replace(/\/+$/, '');
    if (/\/cart\/add$/.test(path)) return 'add';
    if (/\/cart\/change$/.test(path)) return 'change';
    if (/\/cart\/update$/.test(path)) return 'update';
    return null;
  }

  /* ---------------------------------------------------------------------------
   * 3. PAYLOAD PARSING — FormData, URLSearchParams, urlencoded string, JSON.
   * ------------------------------------------------------------------------ */
  function isArray(value) {
    return Object.prototype.toString.call(value) === '[object Array]';
  }

  function parseBody(body) {
    if (body == null || body === '') return { type: 'none' };
    if (typeof FormData !== 'undefined' && body instanceof FormData) return { type: 'entries', data: body };
    if (typeof URLSearchParams !== 'undefined' && body instanceof URLSearchParams) return { type: 'entries', data: body };
    if (typeof body === 'string') {
      var trimmed = body.replace(/^\s+/, '');
      if (trimmed.charAt(0) === '{' || trimmed.charAt(0) === '[') {
        try {
          return { type: 'json', data: JSON.parse(trimmed) };
        } catch (error) {
          /* not JSON after all — fall through to the urlencoded reading */
        }
      }
      try {
        return { type: 'entries', data: new URLSearchParams(body) };
      } catch (error) {
        return { type: 'none' };
      }
    }
    if (typeof body === 'object') return { type: 'json', data: body };
    return { type: 'none' };
  }

  function eachEntry(parsed, visit) {
    if (parsed.type !== 'entries' || !parsed.data || typeof parsed.data.forEach !== 'function') return;
    parsed.data.forEach(function (value, key) {
      visit(String(key), typeof value === 'string' ? value : '');
    });
  }

  function toQuantity(value) {
    var quantity = parseInt(value, 10);
    if (isNaN(quantity) || quantity < 1) return 1;
    return quantity;
  }

  /* The variant a `/cart/change` `id` refers to. Shopify accepts either the bare
     variant id or the line key `<variant_id>:<hash>`; both start with the id. */
  function variantOf(value) {
    return Number(String(value).split(':')[0]) || 0;
  }

  function addedLines(parsed) {
    var lines = [];
    if (parsed.type === 'json') {
      var payload = parsed.data || {};
      if (isArray(payload.items)) {
        for (var i = 0; i < payload.items.length; i++) {
          var entry = payload.items[i] || {};
          if (entry.id != null) lines.push({ id: variantOf(entry.id), quantity: toQuantity(entry.quantity) });
        }
      } else if (payload.id != null) {
        lines.push({ id: variantOf(payload.id), quantity: toQuantity(payload.quantity) });
      }
      return lines;
    }

    var indexed = {};
    var single = {};
    eachEntry(parsed, function (key, value) {
      var match = key.match(/^items\[(\d+)\]\[(id|quantity)\]$/);
      if (match) {
        if (!indexed[match[1]]) indexed[match[1]] = {};
        indexed[match[1]][match[2]] = value;
        return;
      }
      if (key === 'id') single.id = value;
      if (key === 'quantity') single.quantity = value;
    });

    var keys = Object.keys(indexed);
    for (var k = 0; k < keys.length; k++) {
      var line = indexed[keys[k]];
      if (line.id != null) lines.push({ id: variantOf(line.id), quantity: toQuantity(line.quantity) });
    }
    if (!lines.length && single.id != null) {
      lines.push({ id: variantOf(single.id), quantity: toQuantity(single.quantity) });
    }
    return lines;
  }

  /* The quantity a change/update payload WANTS the calendar to end up at, or
     null when the payload does not address the calendar at all. Returning null
     is what keeps the attribution and gift writers untouched. */
  function requestedCalendarQuantity(endpoint, parsed, cart) {
    var items = cart && isArray(cart.items) ? cart.items : null;
    var answer = null;

    function considerKey(key, value) {
      var quantity = parseInt(value, 10);
      if (isNaN(quantity)) return;
      if (variantOf(key) === VARIANT_ID) answer = quantity;
    }

    function considerLineIndex(index, value) {
      var quantity = parseInt(value, 10);
      if (isNaN(quantity) || !items) return;
      var item = items[index - 1];
      if (item && Number(item.variant_id) === VARIANT_ID) answer = quantity;
    }

    if (endpoint === 'change') {
      if (parsed.type === 'json') {
        var payload = parsed.data || {};
        if (payload.id != null) considerKey(payload.id, payload.quantity);
        else if (payload.line != null) considerLineIndex(parseInt(payload.line, 10), payload.quantity);
      } else {
        var change = {};
        eachEntry(parsed, function (key, value) {
          change[key] = value;
        });
        if (change.id != null) considerKey(change.id, change.quantity);
        else if (change.line != null) considerLineIndex(parseInt(change.line, 10), change.quantity);
      }
      return answer;
    }

    /* update: `updates` is either a map keyed by variant id / line key, or an
       array in line order. */
    if (parsed.type === 'json') {
      var updates = (parsed.data || {}).updates;
      if (isArray(updates)) {
        for (var i = 0; i < updates.length; i++) considerLineIndex(i + 1, updates[i]);
      } else if (updates && typeof updates === 'object') {
        var keys = Object.keys(updates);
        for (var k = 0; k < keys.length; k++) considerKey(keys[k], updates[keys[k]]);
      }
      return answer;
    }

    eachEntry(parsed, function (key, value) {
      var match = key.match(/^updates\[(.+)\]$/);
      if (match) considerKey(match[1], value);
      else if (key === 'updates[]') {
        /* Positional array form. Rare, and only resolvable against the cart. */
      }
    });
    return answer;
  }

  /* ---------------------------------------------------------------------------
   * 4. THE VERDICT
   * ------------------------------------------------------------------------ */
  function verdictFor(endpoint, parsed, cart) {
    var state = cart ? adoptCart(cart) || cartState : cartState;

    if (endpoint === 'add') {
      var lines = addedLines(parsed);
      if (!lines.length) return null;

      var addsCalendar = 0;
      var addsOther = false;
      for (var i = 0; i < lines.length; i++) {
        if (lines[i].id === VARIANT_ID) addsCalendar += lines[i].quantity;
        else if (lines[i].id) addsOther = true;
      }

      if (addsCalendar && addsOther) return { kind: 'mixed' };
      if (addsCalendar) {
        if (state.calendarQuantity + addsCalendar > MAX_QTY) return { kind: 'max' };
        if (state.otherCount > 0) return { kind: 'clear' };
        return null;
      }
      if (addsOther && state.calendarQuantity > 0) return { kind: 'remove' };
      return null;
    }

    var wanted = requestedCalendarQuantity(endpoint, parsed, cart);
    if (wanted != null && wanted > MAX_QTY) return { kind: 'max' };
    return null;
  }

  /* Cheap pre-check, from the cache, used to decide whether a request is worth a
     round trip at all. Adding anything other than the calendar to a cart the
     cache says holds no calendar costs nothing. */
  function worthInspecting(endpoint, lines) {
    if (endpoint !== 'add') return cartState.calendarQuantity > 0;
    for (var i = 0; i < lines.length; i++) {
      if (lines[i].id === VARIANT_ID) return true;
    }
    return cartState.calendarQuantity > 0;
  }

  /* ---------------------------------------------------------------------------
   * 5. RESOLUTION — the two things a shopper can choose.
   * ------------------------------------------------------------------------ */
  function cartPost(url, payload) {
    return nativeFetch(url, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    }).then(function (response) {
      return response.ok ? response.json() : null;
    });
  }

  function removeCalendar() {
    return readCart().then(function (cart) {
      var key = (cart && adoptCart(cart) && cartState.lineKey) || cartState.lineKey;
      if (!key) return null;
      return cartPost(ROUTES.change, { id: key, quantity: 0 });
    }).then(function (cart) {
      if (cart) adoptCart(cart);
      return cart;
    });
  }

  function clearCartForCalendar() {
    return readCart().then(function (cart) {
      rememberRemoved(cart);
      return nativeFetch(ROUTES.clear, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: '{}'
      });
    }).then(function (response) {
      return response && response.ok ? response.json() : null;
    }).then(function (cart) {
      if (cart) adoptCart(cart);
      else cartState = { calendarQuantity: 0, otherCount: 0, lineKey: '' };
      return cart;
    });
  }

  /* The removed titles are remembered for the session so the cart can say what
     was taken out. Restoring them is deliberately out of scope (non-goal). */
  function rememberRemoved(cart) {
    if (!cart || !isArray(cart.items)) return;
    var titles = [];
    for (var i = 0; i < cart.items.length; i++) {
      var item = cart.items[i];
      if (Number(item.variant_id) === VARIANT_ID) continue;
      titles.push(item.product_title || item.title || '');
    }
    try {
      if (titles.length) window.sessionStorage.setItem(REMOVED_KEY, JSON.stringify(titles));
    } catch (error) {
      /* private mode / storage disabled — the note simply does not appear */
    }
  }

  function forgetRemoved() {
    try {
      window.sessionStorage.removeItem(REMOVED_KEY);
    } catch (error) {
      /* nothing to do */
    }
  }

  /* ---------------------------------------------------------------------------
   * 6. UI
   * ------------------------------------------------------------------------ */
  var modal = null;
  var lastFocus = null;

  function buildModal() {
    if (modal) return modal;
    var root = document.createElement('div');
    root.className = 'ak-lock-modal';
    root.setAttribute('data-ak-lock-modal', '');
    root.hidden = true;
    root.innerHTML =
      '<div class="ak-lock-modal__overlay" data-ak-lock-dismiss></div>' +
      '<div class="ak-lock-modal__dialog" role="dialog" aria-modal="true" aria-labelledby="AkLockTitle" tabindex="-1">' +
      '<button type="button" class="ak-lock-modal__close" data-ak-lock-dismiss></button>' +
      '<p class="ak-lock-modal__title" id="AkLockTitle"></p>' +
      '<p class="ak-lock-modal__body" data-ak-lock-body></p>' +
      '<div class="ak-lock-modal__actions">' +
      '<button type="button" class="button button--primary ak-lock-modal__confirm" data-ak-lock-confirm></button>' +
      '<button type="button" class="button button--secondary ak-lock-modal__cancel" data-ak-lock-cancel></button>' +
      '</div></div>';
    document.body.appendChild(root);

    var closeButton = root.querySelector('.ak-lock-modal__close');
    closeButton.setAttribute('aria-label', STRINGS.close || '');
    closeButton.textContent = '×';
    root.querySelector('#AkLockTitle').textContent = STRINGS.title || '';

    root.addEventListener('click', function (event) {
      if (event.target.hasAttribute('data-ak-lock-dismiss')) closeModal('cancel');
    });
    root.addEventListener('keydown', function (event) {
      if (event.key === 'Escape' || event.key === 'Esc') {
        event.preventDefault();
        closeModal('cancel');
        return;
      }
      if (event.key !== 'Tab') return;
      var focusable = root.querySelectorAll('button:not([hidden])');
      if (!focusable.length) return;
      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    modal = root;
    return modal;
  }

  var activeResolve = null;

  function closeModal(answer) {
    if (!modal || modal.hidden) return;
    modal.hidden = true;
    document.documentElement.classList.remove('ak-lock-open');
    var resolve = activeResolve;
    activeResolve = null;
    if (lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
    lastFocus = null;
    if (resolve) resolve(answer === 'confirm');
  }

  function askModal(kind) {
    var root = buildModal();
    var body = root.querySelector('[data-ak-lock-body]');
    var confirm = root.querySelector('[data-ak-lock-confirm]');
    var cancel = root.querySelector('[data-ak-lock-cancel]');

    if (kind === 'remove') {
      body.textContent = STRINGS.bodyRemove || '';
      confirm.textContent = STRINGS.confirmRemove || '';
      cancel.textContent = STRINGS.cancelRemove || '';
    } else {
      body.textContent = STRINGS.bodyClear || '';
      confirm.textContent = STRINGS.confirmClear || '';
      cancel.textContent = STRINGS.cancelClear || '';
    }

    confirm.onclick = function () {
      closeModal('confirm');
    };
    cancel.onclick = function () {
      closeModal('cancel');
    };

    lastFocus = document.activeElement;
    root.hidden = false;
    document.documentElement.classList.add('ak-lock-open');
    root.querySelector('.ak-lock-modal__dialog').focus();

    return new Promise(function (resolve) {
      activeResolve = resolve;
    });
  }

  var toast = null;

  /* Find the inline hint by walking OUTWARD from whatever triggered the notice.
     The hint is a SIBLING of <quantity-input>, not a child of it, and on a
     product page the quantity block and the buy button do not even share a
     form — so a fixed list of ancestor selectors finds it from one trigger and
     misses it from the other. Walking out, then falling back to the document,
     finds it from both. The toast is only for triggers with no DOM at all,
     which means the network layer. */
  function showNotice(message, anchor) {
    if (!message) return;
    var hint = null;
    var node = anchor;
    while (node && node.querySelector && !hint) {
      hint = node.querySelector('[data-ak-lock-max-hint]');
      if (node === document.body) break;
      node = node.parentNode;
    }
    if (!hint) hint = document.querySelector('[data-ak-lock-max-hint]');
    if (hint) {
      hint.textContent = message;
      hint.hidden = false;
      window.clearTimeout(hint.akTimer);
      hint.akTimer = window.setTimeout(function () {
        hint.hidden = true;
      }, 6000);
      return;
    }
    if (!toast) {
      toast = document.createElement('p');
      toast.className = 'ak-lock-toast';
      toast.setAttribute('role', 'status');
      document.body.appendChild(toast);
    }
    toast.textContent = message;
    toast.classList.add('ak-lock-toast--visible');
    window.clearTimeout(toast.akTimer);
    toast.akTimer = window.setTimeout(function () {
      toast.classList.remove('ak-lock-toast--visible');
    }, 6000);
  }

  function renderRemovedNote() {
    var nodes = document.querySelectorAll('[data-ak-lock-removed-note]');
    if (!nodes.length) return;
    var titles = [];
    try {
      titles = JSON.parse(window.sessionStorage.getItem(REMOVED_KEY) || '[]') || [];
    } catch (error) {
      titles = [];
    }
    /* The note belongs to the cart it was made for. Once the calendar is gone
       the note has nothing left to explain. */
    if (!cartState.calendarQuantity) {
      if (titles.length) forgetRemoved();
      titles = [];
    }
    var text = titles.length ? String(STRINGS.removedNote || '').replace('[items]', titles.join(', ')) : '';
    for (var i = 0; i < nodes.length; i++) {
      nodes[i].textContent = text;
      nodes[i].hidden = !text;
    }
  }

  /* The /cart page renders the repair banner OUTSIDE `<form id="cart">` (nested
     forms are dropped by the parser), so it is outside the region an Ajax
     quantity edit re-renders. When the shopper repairs the cart by hand from a
     line's own controls, the banner has to stop claiming otherwise. Only the
     hide direction is needed: entering the forbidden state requires an add
     request, and those never get through. */
  function syncBanner() {
    var banners = document.querySelectorAll('[data-ak-lock-banner]');
    if (!banners.length) return;
    var forbidden =
      (cartState.calendarQuantity > 0 && cartState.otherCount > 0) || cartState.calendarQuantity > MAX_QTY;
    for (var i = 0; i < banners.length; i++) banners[i].hidden = !forbidden;
  }

  function syncCartSurfaces() {
    renderRemovedNote();
    syncBanner();
  }

  /* ---------------------------------------------------------------------------
   * 7. THE SHARED DECISION PATH
   *
   * `replay` re-issues the shopper's original action once the conflict is gone.
   * `cancel` is only ever used by the network layer, which owes its caller a
   * response object.
   * ------------------------------------------------------------------------ */
  function resolveVerdict(verdict, context) {
    if (verdict.kind === 'max') {
      showNotice(STRINGS.maxQty, context.anchor);
      return Promise.resolve(false);
    }
    if (verdict.kind === 'mixed') {
      showNotice(STRINGS.mixedRequest, context.anchor);
      return Promise.resolve(false);
    }
    return askModal(verdict.kind).then(function (confirmed) {
      if (!confirmed) return false;
      var work = verdict.kind === 'remove' ? removeCalendar() : clearCartForCalendar();
      return work.then(function () {
        return true;
      });
    });
  }

  function withBypass(run) {
    bypass += 1;
    try {
      return run();
    } finally {
      window.setTimeout(function () {
        bypass = Math.max(0, bypass - 1);
      }, 0);
    }
  }

  /* ---------------------------------------------------------------------------
   * 8. LAYER 1 — the DOM
   * ------------------------------------------------------------------------ */
  document.addEventListener(
    'submit',
    function (event) {
      if (bypass) return;
      var form = event.target;
      if (!form || form.tagName !== 'FORM') return;
      if (endpointOf(form.getAttribute('action') || '') !== 'add') return;

      var parsed;
      try {
        parsed = { type: 'entries', data: new FormData(form) };
      } catch (error) {
        return;
      }
      var lines = addedLines(parsed);
      if (!lines.length || !worthInspecting('add', lines)) return;

      var submitter = event.submitter || form.querySelector('[type="submit"]');
      event.preventDefault();
      event.stopImmediatePropagation();

      readCart().then(function (cart) {
        var verdict = verdictFor('add', parsed, cart);
        if (!verdict) {
          replaySubmit(form, submitter);
          return;
        }
        resolveVerdict(verdict, { anchor: submitter || form }).then(function (proceed) {
          if (proceed) replaySubmit(form, submitter);
        });
      });
    },
    true
  );

  function replaySubmit(form, submitter) {
    withBypass(function () {
      if (typeof form.requestSubmit === 'function') form.requestSubmit(submitter || undefined);
      else form.submit();
    });
  }

  /* The per-order limit at a quantity stepper. `max="1"` (rendered by
     snippets/quantity-input.liquid) already stops the value from moving; this
     only explains why. */
  document.addEventListener(
    'click',
    function (event) {
      var target = event.target;
      if (!target || !target.closest) return;
      var plus = target.closest('quantity-input [name="plus"]');
      if (!plus) return;
      var wrapper = plus.closest('quantity-input');
      var input = wrapper && wrapper.querySelector('input[type="number"]');
      if (!input) return;
      var variantId = Number(input.getAttribute('data-quantity-variant-id') || input.getAttribute('data-index'));
      if (variantId !== VARIANT_ID) return;
      if ((parseInt(input.value, 10) || 0) < MAX_QTY) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      showNotice(STRINGS.maxQty, wrapper);
    },
    true
  );

  /* The repair banner's forms work without JavaScript (a native POST that ends
     in a redirect). With JavaScript they stay on the page instead. */
  document.addEventListener('submit', function (event) {
    var form = event.target;
    if (!form || !form.getAttribute || !form.getAttribute('data-ak-lock-form')) return;
    if (!nativeFetch) return;
    event.preventDefault();
    var action = form.getAttribute('data-ak-lock-form');
    var work;
    if (action === 'remove-calendar') work = removeCalendar();
    else if (action === 'keep-calendar') work = clearCartForCalendar().then(function () {
      return cartPost(ROUTES.add, { items: [{ id: VARIANT_ID, quantity: 1 }] });
    });
    else work = readCart().then(function () {
      return cartPost(ROUTES.change, { id: cartState.lineKey, quantity: MAX_QTY });
    });
    work.then(function () {
      /* A cart that was already in the forbidden state is repaired rarely and
         from two different surfaces (drawer and /cart). A reload is the one
         refresh that is correct on both without reimplementing either. */
      window.location.reload();
    });
  });

  /* ---------------------------------------------------------------------------
   * 9. LAYER 2 — the network
   * ------------------------------------------------------------------------ */
  function sectionsFrom(parsed) {
    var found = { ids: null, url: null };
    if (parsed.type === 'json') {
      var payload = parsed.data || {};
      if (payload.sections) found.ids = isArray(payload.sections) ? payload.sections.join(',') : String(payload.sections);
      if (payload.sections_url) found.url = String(payload.sections_url);
      return found;
    }
    eachEntry(parsed, function (key, value) {
      if (key === 'sections') found.ids = value;
      if (key === 'sections_url') found.url = value;
    });
    return found;
  }

  /* The cancel answer for a caller this theme does not own: the cart exactly as
     it is, plus the sections the caller asked for, so its render path finds the
     shape it expects. It reports "nothing changed", which is the truth. */
  function cancelResponse(parsed) {
    var wanted = sectionsFrom(parsed);
    return readCart().then(function (cart) {
      var payload = cart || { items: [], item_count: 0 };
      if (!wanted.ids) return payload;
      var url = wanted.url || window.location.pathname;
      return nativeFetch(url + (url.indexOf('?') > -1 ? '&' : '?') + 'sections=' + encodeURIComponent(wanted.ids), {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' }
      })
        .then(function (response) {
          return response.ok ? response.json() : null;
        })
        .then(function (sections) {
          if (sections) payload.sections = sections;
          return payload;
        })
        .catch(function () {
          return payload;
        });
    }).then(function (payload) {
      return new Response(JSON.stringify(payload), {
        status: 200,
        headers: { 'Content-Type': 'application/json' }
      });
    });
  }

  if (nativeFetch) {
    window.fetch = function (input, init) {
      var url = typeof input === 'string' ? input : input && input.url ? input.url : String(input || '');
      var endpoint = endpointOf(url);
      if (!endpoint) return nativeFetch(input, init);
      /* A replay still goes through `pass`, so the response it produces keeps
         the cache current — skipping it here left the cache one mutation
         behind after every confirmed modal. */
      if (bypass) return pass(input, init, endpoint);

      var bodyPromise;
      if (init && init.body != null) bodyPromise = Promise.resolve(init.body);
      else if (typeof Request !== 'undefined' && input instanceof Request) {
        try {
          bodyPromise = input.clone().text();
        } catch (error) {
          bodyPromise = Promise.resolve(null);
        }
      } else bodyPromise = Promise.resolve(null);

      return bodyPromise.then(function (body) {
        var parsed = parseBody(body);
        var lines = endpoint === 'add' ? addedLines(parsed) : [];
        if (endpoint === 'add' && !lines.length) return pass(input, init, endpoint);
        if (!worthInspecting(endpoint, lines)) return pass(input, init, endpoint);

        return readCart().then(function (cart) {
          var verdict = verdictFor(endpoint, parsed, cart);
          if (!verdict) return pass(input, init, endpoint);
          return resolveVerdict(verdict, { anchor: null }).then(function (proceed) {
            if (!proceed) return cancelResponse(parsed);
            return withBypass(function () {
              return pass(input, init, endpoint);
            });
          });
        });
      });
    };
  }

  /* Every cart response the wrapper sees updates the cache, so the cheap
     pre-check stays accurate without polling. */
  function pass(input, init, endpoint) {
    return nativeFetch(input, init).then(function (response) {
      if (endpoint && response && typeof response.clone === 'function') {
        try {
          response
            .clone()
            .json()
            .then(function (payload) {
              if (payload && isArray(payload.items)) adoptCart(payload);
              window.setTimeout(syncCartSurfaces, 0);
            })
            .catch(function () {
              /* not a cart body — nothing to adopt */
            });
        } catch (error) {
          /* body already consumed elsewhere — the next read re-syncs */
        }
      }
      return response;
    });
  }

  /* XHR. Older apps still use it; the theme itself does not. The send is
     DEFERRED rather than cancelled, so an allowed request runs untouched on the
     caller's own object and every callback fires normally. */
  if (typeof XMLHttpRequest !== 'undefined' && nativeFetch) {
    var nativeOpen = XMLHttpRequest.prototype.open;
    var nativeSend = XMLHttpRequest.prototype.send;

    XMLHttpRequest.prototype.open = function (method, url) {
      this.akLockEndpoint = endpointOf(url);
      return nativeOpen.apply(this, arguments);
    };

    XMLHttpRequest.prototype.send = function (body) {
      var xhr = this;
      var endpoint = xhr.akLockEndpoint;
      if (!endpoint || bypass) return nativeSend.apply(xhr, arguments);

      var args = arguments;
      var parsed = parseBody(body);
      var lines = endpoint === 'add' ? addedLines(parsed) : [];
      if (endpoint === 'add' && !lines.length) return nativeSend.apply(xhr, args);
      if (!worthInspecting(endpoint, lines)) return nativeSend.apply(xhr, args);

      readCart().then(function (cart) {
        var verdict = verdictFor(endpoint, parsed, cart);
        if (!verdict) return nativeSend.apply(xhr, args);
        return resolveVerdict(verdict, { anchor: null }).then(function (proceed) {
          if (proceed) return nativeSend.apply(xhr, args);
          return cancelResponse(parsed)
            .then(function (response) {
              return response.text();
            })
            .then(function (text) {
              settleXhr(xhr, text);
            });
        });
      });
      return undefined;
    };
  }

  function settleXhr(xhr, text) {
    var defined = true;
    try {
      Object.defineProperty(xhr, 'readyState', { value: 4, configurable: true });
      Object.defineProperty(xhr, 'status', { value: 200, configurable: true });
      Object.defineProperty(xhr, 'responseText', { value: text, configurable: true });
      Object.defineProperty(xhr, 'response', { value: text, configurable: true });
    } catch (error) {
      defined = false;
    }
    if (!defined) {
      /* The instance would not take a synthetic result. An `error` event is a
         signal every XHR caller already handles; silently hanging is not. */
      xhr.dispatchEvent(new Event('error'));
      xhr.dispatchEvent(new Event('loadend'));
      return;
    }
    xhr.dispatchEvent(new Event('readystatechange'));
    xhr.dispatchEvent(new Event('load'));
    xhr.dispatchEvent(new Event('loadend'));
  }

  /* ---------------------------------------------------------------------------
   * 10. THE CART SURFACES follow every cart re-render.
   * ------------------------------------------------------------------------ */
  function watchCartSurfaces() {
    syncCartSurfaces();
    document.addEventListener('cart-quantity-updated', syncCartSurfaces);
    var drawer = document.querySelector('cart-drawer');
    if (drawer && typeof MutationObserver !== 'undefined') {
      new MutationObserver(function () {
        syncCartSurfaces();
      }).observe(drawer, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watchCartSurfaces, { once: true });
  } else {
    watchCartSurfaces();
  }
})();
